/**
 * Product destinations, and cross-domain attribution for links to them.
 *
 * The URL builders are used inside `href={...}` during render, so they must stay
 * pure and SSR-safe: reading localStorage here would crash the server render, and
 * even guarded it would produce a hydration mismatch (server emits no id, client
 * emits one). Decoration therefore happens at CLICK time -- see
 * useProductLinkTracking -- and the helpers below are the pure half of it.
 */

export type ProductType = "recruitment" | "assessment" | "hrms";

const PROD: Record<ProductType, string> = {
  recruitment: "https://app.hirebee.ai",
  assessment: "https://assessments.hirebee.ai",
  hrms: "https://hrms.hirebee.ai",
};

/** Query params the product apps must read to continue the journey. */
export const HB_SESSION_PARAM = "hb_sid";
export const HB_VISITOR_PARAM = "hb_vid";

const PRODUCT_HOSTS = new Set(
  Object.values(PROD).map((u) => u.replace("https://", "")),
);

/** How each product is named to people reading Analytics. */
export const PRODUCT_LABEL: Record<ProductType, string> = {
  recruitment: "Recruitment",
  assessment: "Assessments",
  hrms: "HRMS",
};

const PRODUCT_BY_HOST = new Map(
  (Object.entries(PROD) as [ProductType, string][]).map(([k, u]) => [u.replace("https://", ""), k]),
);

/** Which product a URL points at, or null when it is not a product link. */
export function productFromUrl(url: string): ProductType | null {
  try {
    return PRODUCT_BY_HOST.get(new URL(url, "https://hirebee.ai").host) ?? null;
  } catch {
    return null;
  }
}

/**
 * The name a product-link click is recorded under, e.g. "Get Started — Recruitment,
 * Professional".
 *
 * get_cta_attribution groups clicks by this name alone. It used to be the link text
 * plus the raw plan key ("Get Started — professional"), and Recruitment, HRMS and
 * Assessments share plan keys -- so their plan clicks merged into one number. The
 * product is part of the name for that reason.
 */
export function productLinkLabel(text: string, url: string): string {
  const label = text.trim().replace(/\s+/g, " ").slice(0, 120) || "Product link";
  const product = productFromUrl(url);
  let plan: string | null = null;
  try {
    plan = new URL(url, "https://hirebee.ai").searchParams.get("plan");
  } catch {
    plan = null;
  }
  const parts = [
    product ? PRODUCT_LABEL[product] : null,
    plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : null,
  ].filter(Boolean);
  return parts.length ? `${label} — ${parts.join(", ")}` : label;
}

/**
 * The href a product link was written with, before this visit's identity was added.
 *
 * useProductLinkTracking rewrites an anchor's href at click time and remembers both the
 * href it started from (raw) and the one it wrote (decorated). The current href still
 * equal to what we wrote means it is ours, so the remembered raw value is the real one.
 * Anything else is React patching the anchor with a new URL -- a pricing calculator's
 * plan follows its slider -- and that new URL is the raw value now. React diffs against
 * the previous prop, not the DOM, so it only writes the href when the URL changed.
 * Caching the first href forever sent a second click to the first click's plan.
 */
export function resolveRawHref(
  entry: { raw: string; decorated: string } | undefined,
  current: string,
): string {
  return entry && current === entry.decorated ? entry.raw : current;
}

export function isProductUrl(url: string): boolean {
  try {
    return PRODUCT_HOSTS.has(new URL(url, "https://hirebee.ai").host);
  } catch {
    return false;
  }
}

/**
 * Attach the landing-site identity to a product URL.
 *
 * `hb_sid` is the session id specifically, because signup_events has session_id
 * and no visitor_id, and session_id is the column every funnel RPC joins on -- so
 * it is the only value that lets a registration be tied back to the visit that
 * produced it. hb_vid rides along for longer-horizon identity stitching, and the
 * first-touch UTMs so the product can attribute the signup to the campaign
 * without re-deriving it.
 *
 * Existing params are preserved and never overwritten: `?plan=` is already load
 * bearing, and a URL that already carries an explicit utm_source (the hrms signin
 * link hardcodes one) keeps it.
 */
export function decorateProductUrl(
  url: string,
  identity: { sessionId?: string | null; visitorId?: string | null; utm?: Record<string, string | undefined> },
): string {
  if (!isProductUrl(url)) return url;

  let parsed: URL;
  try {
    parsed = new URL(url, "https://hirebee.ai");
  } catch {
    return url;
  }

  const setIfAbsent = (k: string, v: string | null | undefined) => {
    if (v && !parsed.searchParams.has(k)) parsed.searchParams.set(k, v);
  };

  setIfAbsent(HB_SESSION_PARAM, identity.sessionId);
  setIfAbsent(HB_VISITOR_PARAM, identity.visitorId);
  for (const [k, v] of Object.entries(identity.utm ?? {})) setIfAbsent(k, v);

  return parsed.toString();
}

export function getProductSignupUrl(product: ProductType, plan?: string): string {
  const domain = PROD[product];
  const planParam = plan ? `?plan=${plan}` : "";
  if (product === "hrms") return `${domain}/register${planParam}`;
  if (product === "recruitment") return `${domain}/auth/signup${planParam}`;
  return `${domain}/signup${planParam}`;
}

export function getProductSignInUrl(product: ProductType, plan?: string): string {
  const domain = PROD[product];
  if (product === "hrms")
    return `${domain}/signin?utm_source=nav&utm_medium=link&utm_campaign=login`;
  const planParam = plan ? `?plan=${plan}` : "";
  if (product === "recruitment") return `${domain}/auth/login${planParam}`;
  return `${domain}/signin${planParam}`;
}

export function getProductLoginUrl(product: ProductType): string {
  return getProductSignInUrl(product);
}

export function getProductDomain(product: ProductType): string {
  return PROD[product].replace("https://", "");
}
